import React from 'react';
import { DocPageLayout } from '../../components/DocPageLayout';
import { CodeBlock } from '../../components/CodeBlock';
import { InfoBox } from '../../components/InfoBox';
export function ApiAgreementPage({
  onNavigate
}: {
  onNavigate?: (id: string) => void;
}) {
  return <DocPageLayout title="Agreement API" description="Allows the merchant to store the payer's payment details and make payments under that agreement without the payer's involvement." breadcrumbs={[{
    label: 'API Reference',
    id: 'api-overview'
  }, {
    label: 'Agreement'
  }]} onNavigate={onNavigate} sections={[{
    id: 'create-agreement',
    title: 'Create Agreement'
  }, {
    id: 'update-agreement',
    title: 'Update Agreement'
  }, {
    id: 'retrieve-agreement',
    title: 'Retrieve Agreement'
  }, {
    id: 'delete-agreement',
    title: 'Delete Agreement'
  }, {
    id: 'parameters',
    title: 'Parameters'
  }]} data-id="element-312">
      <p className="text-gray-700 mb-8" data-id="element-313">
        The Agreement API enables you to create and manage agreements that
        authorize the merchant to charge a payer's stored payment credentials on
        a recurring or installment basis.
      </p>

      <h2 id="create-agreement" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-314">
        Create Agreement
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-315">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 mr-2" data-id="element-316">
          POST
        </span>
        <span className="text-gray-600" data-id="element-317">/merchant/</span>
        <span className="text-blue-600" data-id="element-318">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-319">/token/</span>
        <span className="text-blue-600" data-id="element-320">{'{tokenId}'}</span>
        <span className="text-gray-600" data-id="element-321">/agreement/</span>
        <span className="text-blue-600" data-id="element-322">{'{agreementId}'}</span>
      </div>
      <p className="text-gray-700 mb-4" data-id="element-323">
        Creates a new agreement linked to a stored payment token. The agreement
        defines the terms under which the merchant can initiate payments.
      </p>
      <CodeBlock language="JSON" title="Request Body" code={`{
  "agreement": {
    "type": "RECURRING",
    "numberOfPayments": 12,
    "frequency": "MONTHLY",
    "startDate": "2026-04-01",
    "expiryDate": "2027-03-31"
  },
  "order": {
    "amount": "29.99",
    "currency": "USD",
    "description": "Monthly subscription"
  }
}`} data-id="element-324" />
      <CodeBlock language="JSON" title="Response" code={`{
  "result": "SUCCESS",
  "agreement": {
    "id": "AGR-001",
    "type": "RECURRING",
    "status": "ACTIVE",
    "numberOfPayments": 12,
    "frequency": "MONTHLY",
    "startDate": "2026-04-01",
    "expiryDate": "2027-03-31"
  },
  "token": "9111111111111111"
}`} data-id="element-325" />

      <h2 id="update-agreement" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-326">
        Update Agreement
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-327">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700 mr-2" data-id="element-328">
          PUT
        </span>
        <span className="text-gray-600" data-id="element-329">/merchant/</span>
        <span className="text-blue-600" data-id="element-330">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-331">/token/</span>
        <span className="text-blue-600" data-id="element-332">{'{tokenId}'}</span>
        <span className="text-gray-600" data-id="element-333">/agreement/</span>
        <span className="text-blue-600" data-id="element-334">{'{agreementId}'}</span>
      </div>
      <p className="text-gray-700 mb-4" data-id="element-335">
        Modify an existing agreement's parameters such as amount, frequency, or
        expiry date.
      </p>

      <h2 id="retrieve-agreement" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-336">
        Retrieve Agreement
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-337">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700 mr-2" data-id="element-338">
          GET
        </span>
        <span className="text-gray-600" data-id="element-339">/merchant/</span>
        <span className="text-blue-600" data-id="element-340">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-341">/token/</span>
        <span className="text-blue-600" data-id="element-342">{'{tokenId}'}</span>
        <span className="text-gray-600" data-id="element-343">/agreement/</span>
        <span className="text-blue-600" data-id="element-344">{'{agreementId}'}</span>
      </div>

      <h2 id="delete-agreement" className="text-2xl font-bold text-gray-900 mt-10 mb-4 border-b border-gray-200 pb-2" data-id="element-345">
        Delete Agreement
      </h2>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 font-mono text-sm" data-id="element-346">
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 mr-2" data-id="element-347">
          DELETE
        </span>
        <span className="text-gray-600" data-id="element-348">/merchant/</span>
        <span className="text-blue-600" data-id="element-349">{'{merchantId}'}</span>
        <span className="text-gray-600" data-id="element-350">/token/</span>
        <span className="text-blue-600" data-id="element-351">{'{tokenId}'}</span>
        <span className="text-gray-600" data-id="element-352">/agreement/</span>
        <span className="text-blue-600" data-id="element-353">{'{agreementId}'}</span>
      </div>

      <h2 id="parameters" className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-354">
        Parameters
      </h2>
      <div className="overflow-x-auto mb-8" data-id="element-355">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-356">
          <thead data-id="element-357">
            <tr className="bg-gray-100" data-id="element-358">
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-359">
                Field
              </th>
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-360">
                Type
              </th>
              <th className="text-left px-4 py-3 font-semibold border-b" data-id="element-361">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-362">
            <tr data-id="element-363">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-364">
                agreement.type <span className="text-red-500" data-id="element-365">*</span>
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-366">Enum</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-367">
                RECURRING or INSTALLMENT
              </td>
            </tr>
            <tr className="bg-gray-50" data-id="element-368">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-369">
                agreement.numberOfPayments
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-370">Integer</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-371">
                Total number of payments in the agreement
              </td>
            </tr>
            <tr data-id="element-372">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-373">
                agreement.frequency
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-374">Enum</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-375">
                DAILY, WEEKLY, MONTHLY, QUARTERLY, YEARLY
              </td>
            </tr>
            <tr className="bg-gray-50" data-id="element-376">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-377">
                agreement.startDate
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-378">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-379">
                ISO 8601 date for the first payment
              </td>
            </tr>
            <tr data-id="element-380">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-381">
                agreement.expiryDate
              </td>
              <td className="px-4 py-3 text-gray-600" data-id="element-382">String</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-383">
                ISO 8601 date when the agreement expires
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <InfoBox type="info" title="Token Required" data-id="element-384">
        Agreements must be linked to a valid payment token. Create a token first
        using the Tokenization API before creating an agreement.
      </InfoBox>
    </DocPageLayout>;
}